import { useTranslation } from 'react-i18next';
import { haptic } from '../../hooks/useTelegram';
import { useConfig } from '../../hooks/useConfig';
import { mockSupport } from '../../lib/mock';
import { FigIcon, type FigIconName } from '../FigIcon';
import { ChevronRight } from '../icons';

function Card({
  icon,
  title,
  subtitle,
  onClick,
  href,
}: {
  icon: FigIconName;
  title: string;
  subtitle: string;
  onClick?: () => void;
  href?: string;
}) {
  const inner = (
    <>
      <span className="grid size-11 shrink-0 place-items-center rounded-[14px] bg-surface">
        <FigIcon name={icon} size={20} className="text-ink" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-[15px] font-medium text-ink">{title}</span>
        <span className="truncate text-[13px] text-muted">{subtitle}</span>
      </span>
      <ChevronRight width={18} height={18} className="text-muted" />
    </>
  );
  const cls =
    'flex w-full items-center gap-3 rounded-card bg-card p-4 text-left shadow-card active:opacity-70';
  return href ? (
    <a href={href} className={cls}>
      {inner}
    </a>
  ) : (
    <button onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}

/** Support screen (Profile → Support) — contact channels. Ported from the
 *  reference wallet; no dedicated Figma design. */
export function SupportScreen() {
  const { t } = useTranslation();
  const { config } = useConfig();
  // Server-managed (admin-editable) contacts; mock while the config loads.
  const support = config?.support ?? { telegram: mockSupport.telegram, email: mockSupport.email };

  const openTelegram = () => {
    haptic();
    const wa = window.Telegram?.WebApp as unknown as {
      openTelegramLink?: (u: string) => void;
    } | undefined;
    if (wa?.openTelegramLink) wa.openTelegramLink(support.telegram);
    else window.open(support.telegram, '_blank');
  };

  return (
    <div className="push-enter flex min-h-full flex-col bg-surface px-4 pb-16 pt-3">
      <header className="flex items-center py-2">
        <h1 className="text-[20px] font-medium text-ink">{t('profile.support')}</h1>
      </header>

      <p className="mb-4 mt-1 text-[13px] text-muted">{t('help.supportHint')}</p>

      <div className="flex flex-col gap-3">
        <Card
          icon="pfSupport"
          title={t('help.chat')}
          subtitle={t('help.chatDesc')}
          onClick={openTelegram}
        />
        <Card
          icon="pfEmail"
          title={t('help.email')}
          subtitle={support.email}
          href={`mailto:${support.email}`}
        />
      </div>
    </div>
  );
}
